import React, { useEffect, useState } from "react";
import { api, errorMessage } from "../../lib/api";
import { toast } from "sonner";
import { Crown, ChevronDown, ChevronRight } from "lucide-react";
import { JudgeScoresList } from "./JudgeScoreCard";

export default function ScoringPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openKey, setOpenKey] = useState(null);            // `${team_id}-${round}` currently expanded
  const [scoresByTeam, setScoresByTeam] = useState({});    // { [team_id]: [...scores] }
  const [scoreLoading, setScoreLoading] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/scoring"); setData(data); } catch (err) { toast.error(errorMessage(err)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleRow = async (teamId, round) => {
    const key = `${teamId}-${round}`;
    if (openKey === key) { setOpenKey(null); return; }
    setOpenKey(key);
    if (!scoresByTeam[teamId]) {
      setScoreLoading(teamId);
      try {
        const { data } = await api.get(`/admin/teams/${teamId}/scores`);
        setScoresByTeam((m) => ({ ...m, [teamId]: data.scores || [] }));
      } catch (err) { toast.error(errorMessage(err)); }
      finally { setScoreLoading(null); }
    }
  };

  const setWinners = async (round) => {
    if (!window.confirm(`Auto-set top 3 from Round ${round} as winners?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/set-winners", { round });
      toast.success(`${data.winners.length} winner(s) set`);
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  if (!data) return <div className="card-surface"><span className="spinner" /></div>;
  return (
    <div data-testid="scoring-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="label-mono">Average score per team · min judges required: <span className="text-brand">{data.min_judges_required}</span></div>
        <div className="flex gap-2">
          <button onClick={() => setWinners(2)} disabled={busy} className="btn-primary !py-2 !px-3 text-xs flex items-center gap-2" data-testid="set-winners-r2-btn">
            <Crown className="w-3.5 h-3.5" /> Set Top 3 (Round 2)
          </button>
          <button onClick={() => setWinners(1)} disabled={busy} className="btn-secondary !py-2 !px-3 text-xs">
            Set Top 3 (Round 1)
          </button>
        </div>
      </div>
      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="label-mono text-left border-b border-white/10">
              <th className="py-3 pl-2 pr-4 w-8"></th>
              <th className="py-3 pr-4">Team</th><th className="py-3 pr-4">Round</th>
              <th className="py-3 pr-4">Status</th><th className="py-3 pr-4">Judges</th><th className="py-3 pr-4">Avg Score</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r, i) => {
              const key = `${r.team_id}-${r.round}`;
              const isOpen = openKey === key;
              const scores = (scoresByTeam[r.team_id] || []).filter((s) => s.round === r.round);
              return (
                <React.Fragment key={key}>
                  <tr
                    onClick={() => toggleRow(r.team_id, r.round)}
                    className={`border-b border-white/5 cursor-pointer hover:bg-white/[0.02] ${r.meets_min ? "" : "opacity-60"}`}
                    data-testid={`scoring-row-${i}`}
                  >
                    <td className="py-3 pl-2 pr-4 text-gray-400">
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </td>
                    <td className="py-3 pr-4 text-white">{r.team_name}</td>
                    <td className="py-3 pr-4 font-mono text-xs">R{r.round}</td>
                    <td className="py-3 pr-4"><span className={`badge badge-${r.team_status || "upcoming"}`}>{r.team_status || "—"}</span></td>
                    <td className="py-3 pr-4 font-mono text-xs">{r.judges_count} / {data.min_judges_required}</td>
                    <td className="py-3 pr-4 font-display text-brand text-lg">{r.average_score.toFixed(2)}</td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-white/[0.015]">
                      <td colSpan={6} className="py-5 px-6 border-b border-white/10">
                        {scoreLoading === r.team_id ? (
                          <div className="text-center py-4"><span className="spinner" /></div>
                        ) : (
                          <JudgeScoresList scores={scores} />
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {data.rows.length === 0 && <tr><td colSpan={6} className="text-center text-gray-500 py-8">No scores yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
